import { createTheme } from '@mui/material/styles';

// Палитра и токены извлечены из Figma-макета (см. theme.css)
const brand        = '#F08F5A';
const brandDeep    = '#E87537';
const brandStrong  = '#FD7F3A';
const bgPage       = '#EFE6DA';
const bgCard       = '#F4ECE3';
const bgLight      = '#F8F2EA';
const fg           = '#382F2A';
const fgMuted      = '#605148';
const fgDisabled   = '#A59992';
const danger       = '#EB5757';
const success      = '#4AA587';
const warning      = '#F1A11D';
const info         = '#00579F';
const border       = 'rgba(56, 47, 42, 0.10)';
const borderStrong = 'rgba(56, 47, 42, 0.16)';

const muiTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: brand,
      dark: brandDeep,
      light: brandStrong,
      contrastText: '#fff',
    },
    secondary: {
      main: fgMuted,
      dark: fg,
      light: fgDisabled,
      contrastText: '#fff',
    },
    error:   { main: danger },
    success: { main: success },
    warning: { main: warning },
    info:    { main: info },
    background: {
      default: bgPage,
      paper:   bgCard,
    },
    text: {
      primary:   fg,
      secondary: fgMuted,
      disabled:  fgDisabled,
    },
    divider: border,
  },
  shape: {
    borderRadius: 12,
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", Roboto, system-ui, -apple-system, Arial, sans-serif',
    h1: { fontWeight: 700, letterSpacing: '-0.01em' },
    h2: { fontWeight: 700, letterSpacing: '-0.01em' },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 600 },
    button:    { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: bgPage, color: fg },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          paddingInline: 18,
        },
        containedPrimary: {
          background: brand,
          boxShadow: '0 6px 14px -4px rgba(240, 143, 90, 0.40)',
          '&:hover': {
            background: brandDeep,
            boxShadow: '0 10px 22px -6px rgba(240, 143, 90, 0.55)',
          },
        },
        outlined: {
          borderColor: borderStrong,
          color: fg,
          '&:hover': { borderColor: fgDisabled, background: bgLight },
        },
        text: {
          color: brandDeep,
          '&:hover': { background: 'rgba(240, 143, 90, 0.10)' },
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small', variant: 'outlined' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
          borderRadius: 12,
          '& fieldset': { borderColor: 'rgba(56,47,42,0.14)' },
          '&:hover fieldset': { borderColor: fgDisabled },
          '&.Mui-focused fieldset': {
            borderColor: brand,
            boxShadow: '0 0 0 4px rgba(240, 143, 90, 0.16)',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { color: fgMuted, '&.Mui-focused': { color: brandDeep } },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 16 },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: '0 6px 16px -4px rgba(56, 47, 42, 0.10)',
          border: `1px solid ${border}`,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: { padding: 24, '&:last-child': { paddingBottom: 24 } },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, fontSize: 12 },
        filled: { background: 'rgba(240, 143, 90, 0.14)', color: brandDeep },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(244, 236, 227, 0.85)',
          backdropFilter: 'blur(10px)',
          color: fg,
          borderBottom: `1px solid ${border}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: '#564E44',
          color: '#fff',
          borderRight: 'none',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          background: bgCard,
          boxShadow: '0 24px 64px -16px rgba(56, 47, 42, 0.35), 0 8px 24px -8px rgba(56, 47, 42, 0.18)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontWeight: 700, fontSize: 18, letterSpacing: '-0.01em' },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: { background: bgCard, borderRadius: 16, overflow: 'hidden' },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(90deg, #FF965C, #F37D3C)',
          '& th': { color: '#fff', fontWeight: 600, border: 'none' },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(odd)':  { background: '#FBECD9' },
          '&:nth-of-type(even)': { background: '#E9DDCF' },
          '&:hover':             { background: 'rgba(240, 143, 90, 0.12)' },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderTop: `1px solid ${border}`, fontSize: 14 },
        head: { background: 'transparent' },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        thumb: { background: '#fff' },
        track: { background: fgDisabled, opacity: 1 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardSuccess: { background: 'rgba(74,165,135,0.10)', color: '#0F5B3C' },
        standardError:   { background: 'rgba(235,87,87,0.10)',  color: '#7F1D1D' },
        standardInfo:    { background: 'rgba(240,143,90,0.10)', color: '#7F3B1A' },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: fg,
          color: '#fff',
          fontSize: 12,
          fontWeight: 500,
          borderRadius: 8,
          padding: '6px 10px',
        },
      },
    },
  },
});

export default muiTheme;
